import type { FirestoreLead } from './firestoreLead.types';
import type { LeadKind } from './mockDashboardData';

/** Classify lead for buyer vs rental/lease pricing (keep logic in one place). */
export function deriveLeadKind(doc: {
  emailSource?: string | null;
  propertyPrice?: string | null;
  showingType?: string | null;
}): LeadKind {
  const src = (doc.emailSource ?? '').toUpperCase();
  if (
    src.includes('SHOWING') ||
    src.includes('LEASE') ||
    src.includes('RENT')
  ) {
    return 'lease';
  }
  const price = parseFloat(
    String(doc.propertyPrice ?? '').replace(/[^0-9.]/g, '')
  );
  if (Number.isFinite(price) && price > 0 && price < 50_000) {
    return 'lease';
  }
  return 'buyer';
}

function formatPhone(raw: string | undefined): string {
  if (!raw) return '—';
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return raw;
}

function pickCreatedAt(doc: FirestoreLead): string {
  const raw = doc.submittedAt ?? doc.createdAt ?? doc.processedAt;
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10);
  return d.toISOString().slice(0, 10);
}

/** MLS + price summary for the evaluation column */
export function formatEvaluation(doc: FirestoreLead): string {
  const mls = doc.mlsNumber ? `MLS ${doc.mlsNumber}` : 'Listing';
  const price = doc.propertyPrice?.trim();
  if (price) return `${mls} · $${price}`;
  return mls;
}

export interface LeadFeedRow {
  assignmentId: string;
  leadId: string;
  isActive: boolean;
  mlsNumber?: string;
  propertyPrice?: string;
  phone: string;
  name: string;
  email: string;
  address: string;
  propertyType: string;
  leadType: LeadKind;
  evaluation: string;
  createdAt: string;
  leadCreatedAtIso?: string | null;
  purchasedAtIso?: string | null;
  /** True when purchased via Clearance checkout or clearance auto-purchase. */
  isClearance?: boolean;
}

export function firestoreLeadToFeedRow(
  doc: FirestoreLead,
  assignment: {
    id: string;
    isActive: boolean;
  }
): LeadFeedRow {
  return {
    assignmentId: assignment.id,
    leadId: doc.id,
    isActive: assignment.isActive,
    mlsNumber: doc.mlsNumber ?? undefined,
    propertyPrice: doc.propertyPrice ?? undefined,
    phone: formatPhone(doc.visitorPhone),
    name: doc.visitorName?.trim() || '—',
    email: doc.visitorEmail?.trim() || '—',
    address: doc.propertyAddress?.trim() || '—',
    propertyType: doc.propertyType?.trim() || '—',
    leadType: deriveLeadKind(doc),
    evaluation: formatEvaluation(doc),
    createdAt: pickCreatedAt(doc),
    leadCreatedAtIso:
      doc.submittedAt ?? doc.createdAt ?? doc.processedAt ?? null,
    purchasedAtIso: null,
    isClearance: false
  };
}

/** Same filter + sort as the My Leads table (newest purchase first). */
export function sortLeadsForFeed(
  rows: LeadFeedRow[],
  leadTypeFilter: 'all' | LeadKind
): LeadFeedRow[] {
  const out =
    leadTypeFilter === 'all'
      ? rows
      : rows.filter(r => r.leadType === leadTypeFilter);
  return [...out].sort((a, b) => {
    const aTs = a.purchasedAtIso ? new Date(a.purchasedAtIso).getTime() : 0;
    const bTs = b.purchasedAtIso ? new Date(b.purchasedAtIso).getTime() : 0;
    return bTs - aTs;
  });
}
